import { toSpaceId } from "@circle-react/components/QuickPost/useQuickPostSpaceId";
import {
  CREATE_POST_SETTINGS_DEFAULTS,
  pickPostSettingsFormValues,
} from "@circle-react/components/QuickPostV3/data/postSettingsFields";
import type {
  EditablePost,
  PersistedDraft,
  PostFormValues,
  TiptapBody,
} from "@circle-react/components/QuickPostV3/data/types";

interface CreatePostFormDefaultsOptions {
  currentUserId?: number;
  defaultSpaceId?: number;
  persistedDraft?: PersistedDraft;
  tiptapBodyForEditor?: TiptapBody;
  title?: string;
  variant: "create";
}

interface EditPostFormDefaultsOptions {
  post: EditablePost;
  variant: "edit";
}

type PostFormDefaultsOptions =
  | CreatePostFormDefaultsOptions
  | EditPostFormDefaultsOptions;

interface PostFormDefaults {
  defaultValues: PostFormValues;
  shouldShowTipTap: boolean;
}

const createEmptyTiptapBody = (): TiptapBody => ({
  body: {
    type: "doc",
    content: [{ type: "paragraph" }],
  },
  inline_attachments: [],
  sgids_to_object_map: {},
});

export const getPostFormDefaults = (
  options: PostFormDefaultsOptions,
): PostFormDefaults => {
  if (options.variant === "create") {
    const {
      currentUserId,
      defaultSpaceId,
      persistedDraft,
      tiptapBodyForEditor,
      title,
    } = options;

    return {
      defaultValues: {
        ...CREATE_POST_SETTINGS_DEFAULTS,
        cover_image: persistedDraft?.cover_image,
        id: null,
        name: title || persistedDraft?.name || "",
        published_at: null,
        space_id: defaultSpaceId ?? toSpaceId(persistedDraft?.space_id),
        status: "published",
        user_id: currentUserId,
        tiptap_body:
          tiptapBodyForEditor ||
          persistedDraft?.tiptap_body ||
          createEmptyTiptapBody(),
        topics: [],
      },
      shouldShowTipTap: true,
    };
  }

  const { post } = options;

  return {
    defaultValues: {
      ...pickPostSettingsFormValues(post),
      body: post.body_for_editor ?? post.body ?? "",
      cover_image: post.cover_image,
      id: post.id,
      name: post.name ?? "",
      slug: post.slug,
      published_at: post.published_at ?? null,
      space_id: post.space_id,
      status: post.status ?? "published",
      tiptap_body: post.tiptap_body ?? undefined,
      topics: post.topics ?? [],
    },
    shouldShowTipTap: post.editor === "tiptap",
  };
};
